/**
 * GeneratingOverlay - Progress state during audio generation
 *
 * Shows progress bar, status, and optional thinking bubble
 * Uses consistent typography with the rest of the framework
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Loader2, Sparkles } from 'lucide-react';

interface GeneratingOverlayProps {
  progress: number;
  status?: 'GENERATING' | 'ALIGNING' | 'PENDING' | string;
  thinking?: string | null;
  script?: string;
  onCancel?: () => void;
  className?: string;
}

export const GeneratingOverlay: React.FC<GeneratingOverlayProps> = ({
  progress,
  status,
  thinking,
  script: _script,
  className,
}) => {
  // Determine status message
  const statusMessage = React.useMemo(() => {
    if (status === 'ALIGNING') return 'Processing alignment...';
    if (progress >= 100) return 'Complete!';
    if (progress >= 50) return 'Aligning words...';
    return 'Generating speech...';
  }, [status, progress]);

  return (
    <div className={cn(
      'rounded-lg border border-primary/30 bg-primary/5 overflow-hidden',
      className
    )}>
      <div className="px-3 py-2.5 space-y-2">
        {/* Status Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Sparkles className="h-4 w-4 text-primary" />
              <div className="absolute inset-0 animate-ping">
                <Sparkles className="h-4 w-4 text-primary/30" />
              </div>
            </div>
            <span className="text-xs font-medium text-foreground flex items-center gap-2">
              {statusMessage}
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            </span>
          </div>
          <span className="text-[11px] tabular-nums text-muted-foreground font-medium">
            {Math.round(progress)}%
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
            style={{ width: `${Math.max(progress, 5)}%` }}
          />
        </div>

        {/* Thinking Bubble */}
        {thinking && (
          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 bg-muted/30 rounded-md px-2.5 py-1.5">
            {thinking}
          </p>
        )}
      </div>
    </div>
  );
};

export default GeneratingOverlay;
